class InfoCalloutComponent extends HTMLElement {
  /** @type {HTMLButtonElement|null} */
  get #trigger() {
    return this.querySelector('.info-callout__trigger');
  }

  /** @type {HTMLElement|null} */
  get #tooltip() {
    return this.querySelector('.info-callout__tooltip');
  }

  /** @type {HTMLElement|null} */
  get #tooltipInner() {
    return this.querySelector('.info-callout__tooltip-inner');
  }

  #controller = new AbortController();

  /**
   * Sets up open/close listeners.
   * Hover and focusin open the tooltip; click toggles it open (with focus moved inside)
   * or closed. Escape and outside clicks also dismiss, restoring focus to the trigger
   * when it was inside the tooltip. When data-trigger-target="section" the entire
   * component is the hover/focus target.
   */
  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const tooltip = this.#tooltip;
    if (!tooltip) return;

    const trigger = this.#trigger;
    const isSection = this.dataset.triggerTarget === 'section';
    const hoverTarget = isSection ? this : trigger;

    hoverTarget?.addEventListener('pointerenter', (e) => {
      if (e.pointerType !== 'mouse') return;
      this.#open();
    }, { signal });
    trigger?.addEventListener('click', () => {
      if (tooltip.hidden) {
        this.#open(true);
      } else {
        this.#close();
        trigger.focus();
      }
    }, { signal });
    if (isSection) {
      this.addEventListener('focusin', () => this.#open(), { signal });
    }
    tooltip.addEventListener('click', this.#onTooltipClick, { signal });
    // Component-level Escape covers both trigger modes (WCAG SC 1.4.13).
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { this.#close(); trigger?.focus(); }
    }, { signal });
    // Dismiss on any click outside the trigger, including clicks inside the tooltip
    // itself; a click on the trigger is handled by the toggle above. Restores focus to
    // the trigger if it was inside the tooltip, so keyboard/AT users aren't silently
    // dropped to <body> when their focused element is hidden.
    document.addEventListener('click', (event) => {
      const target = /** @type {Node} */ (event.target);
      const withinTriggerSurface = isSection ? this.contains(target) && !tooltip.contains(target) : trigger?.contains(target);
      if (withinTriggerSurface) return;
      const hadFocusInside = tooltip.contains(document.activeElement);
      this.#close();
      if (hadFocusInside) trigger?.focus();
    }, { signal });
  }

  /**
   * Cleans up event listeners.
   */
  disconnectedCallback() {
    this.#controller.abort();
  }

  /** @param {MouseEvent} event */
  #onTooltipClick = (event) => {
    if (/** @type {Element} */ (event.target).closest('.info-callout__close')) {
      this.#close();
      this.#trigger?.focus();
    }
  };

  /**
   * Shows the tooltip, positions the arrow under the trigger centre,
   * and optionally moves focus inside the dialog.
   * @param {boolean} [moveFocus=false] - Move focus to the close button (keyboard open path).
   */
  #open(moveFocus = false) {
    const tooltip = this.#tooltip;
    const tooltipInner = this.#tooltipInner;
    if (!tooltip) return;

    const trigger = this.#trigger;
    if (trigger) {
      const parentRect = this.getBoundingClientRect();
      const triggerRect = trigger.getBoundingClientRect();
      const arrowLeft = triggerRect.left + triggerRect.width / 2 - parentRect.left;
      tooltipInner?.style.setProperty('--tooltip-arrow-left', `${arrowLeft}px`);
      trigger.setAttribute('aria-expanded', 'true');
    }

    tooltip.hidden = false;
    if (moveFocus) {
      this.querySelector('.info-callout__close')?.focus();
    }
  }

  /**
   * Hides the tooltip.
   */
  #close() {
    const tooltip = this.#tooltip;
    if (!tooltip) return;
    tooltip.hidden = true;
    this.#trigger?.setAttribute('aria-expanded', 'false');
  }
}

if (!customElements.get('info-callout-component')) {
  customElements.define('info-callout-component', InfoCalloutComponent);
}
